import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { safeDownloadBlob } from "../lib/downloadUtils";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Beaker, Plus, LayoutGrid, Columns, Archive, Loader2 } from "lucide-react";

import {
  EMPTY_PILOT_FORM, EMPTY_CIERRE,
} from "../components/pilots/pilotUtils";
import { PilotsStatsCards } from "../components/pilots/PilotsStatsCards";
import { PilotsFilters } from "../components/pilots/PilotsFilters";
import { PilotCard } from "../components/pilots/PilotCard";
import { PilotCreateDialog } from "../components/pilots/PilotCreateDialog";
import { PilotEditDialog } from "../components/pilots/PilotEditDialog";
import { PilotDecisionDialog } from "../components/pilots/PilotDecisionDialog";
import { PilotCierreDialog } from "../components/pilots/PilotCierreDialog";
import { PilotsKanban } from "../components/pilots/PilotsKanban";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PilotsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const handlePromoteToProduction = (pilot) => setPromoteTarget(pilot);
  const confirmPromoteToProduction = async () => {
    if (!promoteTarget) return;
    setPromoting(true);
    try {
      const res = await fetch(`${API}/api/productions/from-pilot/${promoteTarget.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Error"); return; }
      toast.success(data.already_promoted ? "Ya estaba promocionado" : "Piloto promocionado a Producción");
      navigate("/production");
    } catch (e) {
      toast.error("Error al promover");
    } finally {
      setPromoting(false);
      setPromoteTarget(null);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [pilots, setPilots] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");

  // Inline partner creation
  const [newPartnerName, setNewPartnerName] = useState("");
  const [creatingPartner, setCreatingPartner] = useState(false);

  // Forms
  const [formData, setFormData] = useState(EMPTY_PILOT_FORM);
  const [editFormData, setEditFormData] = useState(EMPTY_PILOT_FORM);
  const [decisionData, setDecisionData] = useState({ decision: "scale", notes: "" });

  // Cierre state
  const [cierreDialogOpen, setCierreDialogOpen] = useState(false);
  const [cierrePilot, setCierrePilot] = useState(null);
  const [cierreLoading, setCierreLoading] = useState(false);
  const [cierreForm, setCierreForm] = useState(EMPTY_CIERRE);
  const [newKpi, setNewKpi] = useState({ nombre: "", valor: "" });
  const [newLeccion, setNewLeccion] = useState("");

  // View mode
  const [pilotView, setPilotView] = useState("grid"); // "grid" | "kanban"

  const fetchData = useCallback(async () => {
    try {
      const [pilotsData, initiativesData, partnersData] = await Promise.all([
        api.getPilots(token),
        api.getInitiatives(token),
        api.getPartners(token),
      ]);
      setPilots(pilotsData);
      setInitiatives(initiativesData);
      setPartners(partnersData);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Helpers ─────────────────────────────────────────────────────────

  const getInitiativeName = useCallback(
    (id) => initiatives.find((i) => i.id === id)?.name || "Sin iniciativa",
    [initiatives]
  );
  const getInitiativeStatus = useCallback(
    (id) => initiatives.find((i) => i.id === id)?.status || null,
    [initiatives]
  );
  const getPartnerName = useCallback(
    (id) => partners.find((p) => p.id === id)?.name || "-",
    [partners]
  );

  const filteredPilots = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return pilots.filter((p) => {
      const pName = (getPartnerName(p.partner_id) || "").toLowerCase();
      const matchSearch = !q
        || (p.name || "").toLowerCase().includes(q)
        || (p.objective || "").toLowerCase().includes(q)
        || (p.hypothesis || "").toLowerCase().includes(q)
        || pName.includes(q);
      const matchDecision = decisionFilter === "all" || p.decision === decisionFilter;
      const matchPartner = partnerFilter === "all" || p.partner_id === partnerFilter;
      return matchSearch && matchDecision && matchPartner;
    });
  }, [pilots, searchTerm, decisionFilter, partnerFilter, getPartnerName]);

  // ── Handlers ────────────────────────────────────────────────────────

  const handleCreatePilot = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        cost_estimated: formData.cost_estimated ? parseFloat(formData.cost_estimated) : null,
        duration_weeks: parseInt(formData.duration_weeks),
        real_cost_eur: formData.real_cost_eur ? parseFloat(formData.real_cost_eur) : null,
        effort_hours_total: formData.effort_hours_total ? parseFloat(formData.effort_hours_total) : null,
      };
      await api.createPilot(token, payload);
      toast.success("Piloto creado exitosamente");
      setDialogOpen(false);
      setFormData(EMPTY_PILOT_FORM);
      fetchData();
    } catch (error) {
      toast.error("Error al crear piloto");
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenEdit = (pilot) => {
    setSelectedPilot(pilot);
    setEditFormData({
      initiative_id: pilot.initiative_id || "",
      name: pilot.name || "",
      objective: pilot.objective || "",
      hypothesis: pilot.hypothesis || "",
      partner_id: pilot.partner_id || "",
      duration_weeks: pilot.duration_weeks || 8,
      cost_estimated: pilot.cost_estimated || "",
      sponsor: pilot.sponsor || "",
      sponsor_email: pilot.sponsor_email || "",
      business_line: pilot.business_line || "",
      real_cost_eur: pilot.real_cost_eur || "",
      effort_hours_total: pilot.effort_hours_total || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditPilot = async (e) => {
    e.preventDefault();
    if (!selectedPilot) return;
    setFormLoading(true);
    try {
      const payload = {
        ...editFormData,
        cost_estimated: editFormData.cost_estimated ? parseFloat(editFormData.cost_estimated) : null,
        duration_weeks: parseInt(editFormData.duration_weeks),
        real_cost_eur: editFormData.real_cost_eur ? parseFloat(editFormData.real_cost_eur) : null,
        effort_hours_total: editFormData.effort_hours_total ? parseFloat(editFormData.effort_hours_total) : null,
      };
      await api.updatePilot(token, selectedPilot.id, payload);
      toast.success("Piloto actualizado exitosamente");
      setEditDialogOpen(false);
      setSelectedPilot(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar piloto");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePilot = (pilotId, pilotName) => setDeleteTarget({ id: pilotId, name: pilotName });

  const confirmDeletePilot = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await fetch(`${API}/api/pilots/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Piloto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar piloto");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleOpenDecision = (pilot) => {
    setSelectedPilot(pilot);
    setDecisionDialogOpen(true);
  };

  const handleSetDecision = async () => {
    if (!selectedPilot) return;
    setFormLoading(true);
    try {
      await api.setPilotDecision(token, selectedPilot.id, decisionData.decision, decisionData.notes);
      toast.success("Decisión registrada");
      setDecisionDialogOpen(false);
      setSelectedPilot(null);
      fetchData();
    } catch (error) {
      toast.error("Error al registrar decisión");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDownloadFicha = async (pilotId, pilotName, type = "ficha") => {
    try {
      const endpoint = type === "cierre"
        ? `/api/pilots/${pilotId}/cierre/pdf`
        : type === "comercial"
          ? `/api/pilots/${pilotId}/ficha-comercial/pdf`
          : `/api/pilots/${pilotId}/ficha/pdf`;
      const prefix = type === "cierre" ? "Cierre_Piloto_" : type === "comercial" ? "Comercial_Piloto_" : "Ficha_Piloto_";
      const filename = `${prefix}${pilotName.replace(/\s+/g, "_").slice(0, 30)}.pdf`;
      await safeDownloadBlob(`${API}${endpoint}`, filename, token);
      toast.success(type === "comercial" ? "Ficha comercial descargada" : "PDF descargado correctamente");
    } catch (e) {
      toast.error(e.message || "Error al descargar el PDF");
    }
  };

  const handleOpenCierre = async (pilot) => {
    setCierrePilot(pilot);
    setCierreLoading(true);
    setCierreDialogOpen(true);
    try {
      const res = await fetch(`${API}/api/pilots/${pilot.id}/cierre`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCierreForm(Object.keys(data).length ? { ...EMPTY_CIERRE, ...data } : EMPTY_CIERRE);
    } catch {
      setCierreForm(EMPTY_CIERRE);
    } finally {
      setCierreLoading(false);
    }
  };

  const handleSaveCierre = async () => {
    setCierreLoading(true);
    try {
      const res = await fetch(`${API}/api/pilots/${cierrePilot.id}/cierre`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(cierreForm),
      });
      if (!res.ok) throw new Error();
      toast.success("Ficha de cierre guardada");
    } catch {
      toast.error("Error guardando el cierre");
    } finally {
      setCierreLoading(false);
    }
  };

  const handleExportCierre = async () => {
    try {
      const url = `${API}/api/pilots/${cierrePilot.id}/cierre/pdf`;
      const filename = `Cierre_Piloto_${cierrePilot.name.replace(/\s+/g, "_").slice(0, 30)}.pdf`;
      await safeDownloadBlob(url, filename, token);
      toast.success("PDF de cierre descargado");
    } catch (e) {
      toast.error(e.message || "Error exportando el PDF. Guarda primero la ficha.");
    }
  };

  const handleCreatePartnerInline = async (setFieldFn) => {
    if (!newPartnerName.trim()) return;
    setCreatingPartner(true);
    try {
      const created = await api.createPartner(token, { name: newPartnerName.trim(), type: "other" });
      setPartners([...partners, created]);
      setFieldFn(created.id);
      setNewPartnerName("");
      toast.success(`Partner "${created.name}" creado`);
    } catch {
      toast.error("Error al crear partner");
    }
    setCreatingPartner(false);
  };

  const handleKanbanDecisionChange = async (pilotId, newDecision) => {
    // Optimistic update
    setPilots((prev) =>
      prev.map((p) => (p.id === pilotId ? { ...p, decision: newDecision } : p))
    );
    try {
      await api.setPilotDecision(token, pilotId, newDecision, null);
      toast.success(`Piloto movido a "${newDecision === "pending" ? "Pendiente" : newDecision === "scale" ? "Escalar" : newDecision === "extend" ? "Extender" : "Descartar"}"`);
    } catch {
      toast.error("Error al actualizar decisión");
      fetchData(); // revert on error
    }
  };

  const [bulkLoading, setBulkLoading] = useState(false);
  const handleBulkComercialZip = async () => {
    setBulkLoading(true);
    try {
      const url = `${API}/api/pilots-bulk/export-comercial-zip?only_active=true`;
      const filename = `Fichas_Comerciales_Pilotos_${new Date().toISOString().slice(0,10)}.zip`;
      toast.info("Generando ZIP de fichas comerciales de pilotos…");
      await safeDownloadBlob(url, filename, token);
      toast.success("ZIP de fichas comerciales descargado");
    } catch (e) {
      toast.error(e.message || "Error generando el ZIP");
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="pilots-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pilotos Enterprise</h1>
          <p className="text-muted-foreground mt-1">Gestión de pilotos con decisión de escalado</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <Button
              variant={pilotView === "grid" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-3"
              onClick={() => setPilotView("grid")}
              data-testid="pilots-view-grid"
              title="Vista tarjetas"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={pilotView === "kanban" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-3"
              onClick={() => setPilotView("kanban")}
              data-testid="pilots-view-kanban"
              title="Vista Kanban (arrastra para cambiar decisión)"
            >
              <Columns className="w-4 h-4" />
            </Button>
          </div>
          <PilotCreateDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            formData={formData}
            setFormData={setFormData}
            initiatives={initiatives}
            partners={partners}
            newPartnerName={newPartnerName}
            setNewPartnerName={setNewPartnerName}
            creatingPartner={creatingPartner}
            onCreatePartner={handleCreatePartnerInline}
            onSubmit={handleCreatePilot}
            loading={formLoading}
          />
          <Button
            variant="outline"
            onClick={handleBulkComercialZip}
            disabled={bulkLoading}
            data-testid="bulk-comercial-zip-btn"
            title="Descargar ZIP con todas las fichas comerciales de pilotos activos"
          >
            {bulkLoading
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Archive className="w-4 h-4 mr-2" />
            }
            ZIP comerciales
          </Button>
        </div>
      </div>

      <PilotsStatsCards pilots={pilots} />
      <p className="text-xs text-muted-foreground -mt-2" data-testid="pilots-en-curso-note">
        "Pilotos en curso" en Inicio = Pendientes + Extendidos de esta tabla.
      </p>

      <PilotsFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        decisionFilter={decisionFilter} setDecisionFilter={setDecisionFilter}
        partnerFilter={partnerFilter} setPartnerFilter={setPartnerFilter}
        partners={partners}
        filteredCount={filteredPilots.length}
        total={pilots.length}
      />

      {filteredPilots.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center" data-testid="pilots-empty-state">
            <Beaker className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            {pilots.length === 0 ? (
              <>
                <h3 className="text-lg font-medium mb-2">No hay pilotos</h3>
                <p className="text-muted-foreground mb-4">Crea tu primer piloto para comenzar a validar hipótesis</p>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Crear Piloto
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium mb-2">Ningún piloto coincide con estos filtros</h3>
                <p className="text-muted-foreground mb-4">Hay {pilots.length} pilotos en total — prueba a quitar algún filtro</p>
                <Button variant="outline" onClick={() => { setSearchTerm(""); setDecisionFilter("all"); setPartnerFilter("all"); }}>
                  Limpiar filtros
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : pilotView === "kanban" ? (
        <PilotsKanban
          pilots={filteredPilots}
          getInitiativeName={getInitiativeName}
          getPartnerName={getPartnerName}
          onDecisionChange={handleKanbanDecisionChange}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPilots.map((pilot) => (
            <PilotCard
              key={pilot.id}
              pilot={pilot}
              getInitiativeName={getInitiativeName}
              getInitiativeStatus={getInitiativeStatus}
              getPartnerName={getPartnerName}
              onEdit={handleOpenEdit}
              onDelete={handleDeletePilot}
              onDecision={handleOpenDecision}
              onDownloadFicha={handleDownloadFicha}
              onOpenCierre={handleOpenCierre}
              onPromoteToProduction={handlePromoteToProduction}
            />
          ))}
        </div>
      )}

      <PilotDecisionDialog
        open={decisionDialogOpen}
        onOpenChange={setDecisionDialogOpen}
        pilot={selectedPilot}
        decisionData={decisionData}
        setDecisionData={setDecisionData}
        onConfirm={handleSetDecision}
        loading={formLoading}
      />

      <PilotEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        formData={editFormData}
        setFormData={setEditFormData}
        initiatives={initiatives}
        partners={partners}
        newPartnerName={newPartnerName}
        setNewPartnerName={setNewPartnerName}
        creatingPartner={creatingPartner}
        onCreatePartner={handleCreatePartnerInline}
        onSubmit={handleEditPilot}
        loading={formLoading}
      />

      <PilotCierreDialog
        open={cierreDialogOpen}
        onOpenChange={setCierreDialogOpen}
        pilot={cierrePilot}
        form={cierreForm}
        setForm={setCierreForm}
        newKpi={newKpi} setNewKpi={setNewKpi}
        newLeccion={newLeccion} setNewLeccion={setNewLeccion}
        onSave={handleSaveCierre}
        onExport={handleExportCierre}
        loading={cierreLoading}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar piloto"
        description={`¿Estás seguro de que quieres eliminar el piloto "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDeletePilot}
        loading={deleting}
        testId="delete-pilot-confirm-dialog"
      />

      <ConfirmDialog
        open={!!promoteTarget}
        onOpenChange={(v) => !v && setPromoteTarget(null)}
        title="Promover a Producción"
        description={`¿Promover el piloto "${promoteTarget?.name}" a Producción? Pasará a formar parte del seguimiento de producción.`}
        confirmLabel="Promover"
        destructive={false}
        onConfirm={confirmPromoteToProduction}
        loading={promoting}
        testId="promote-pilot-confirm-dialog"
      />
    </div>
  );
}
